import { useMemo } from 'react';

import { keepPreviousData } from '@tanstack/react-query';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { SelectOption } from '@/components/filters/selectUtils';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useTenant } from '@/providers/TenantProvider';
import { uniqueSortedStrings } from '@/utils/sort';

type ForecastSolutionRecord = {
  solution_id?: string;
  model_name?: string;
  model_version?: string;
  fct?: string;
  output_frequency?: string;
  output_hierarchy?: string;
  target?: string;
  leadtime_spans?: number[][];
};

type ForecastSolutionsListResponse = {
  forecast_solutions?: ForecastSolutionRecord[];
};

export type ForecastFacets = {
  customers: SelectOption[];
  forecastCreationTimes: string[];
  frequency: string[];
  frequencyByModelAndVersion: Record<string, string[]>;
  hierarchy: string[];
  hierarchyByModel: Record<string, string[]>;
  leadtimeSpans: Array<[number, number]>;
  leadtimeSpansByModel: Record<string, Array<[number, number]>>;
  modelName: string[];
  modelVersion: string[];
  modelVersionByName: Record<string, string[]>;
  products: SelectOption[];
  target: string[];
};

const EMPTY_FACETS: ForecastFacets = {
  customers: [],
  forecastCreationTimes: [],
  frequency: [],
  frequencyByModelAndVersion: {},
  hierarchy: [],
  hierarchyByModel: {},
  leadtimeSpans: [],
  leadtimeSpansByModel: {},
  modelName: [],
  modelVersion: [],
  modelVersionByName: {},
  products: [],
  target: [],
};

function toLeadtimeSpans(solution: ForecastSolutionRecord): Array<[number, number]> {
  if (!Array.isArray(solution.leadtime_spans)) return [];

  return solution.leadtime_spans.flatMap((item) => {
    if (!Array.isArray(item) || item.length !== 2) return [];
    const leadTime = Number(item[0]);
    const span = Number(item[1]);
    if (!Number.isFinite(leadTime) || !Number.isFinite(span)) return [];
    return [[leadTime, span] as [number, number]];
  });
}

export function useForecastFacets({ enabled = true }: { enabled?: boolean } = {}) {
  const { tenantAlias } = useTenant();
  const { state } = useForecastQueryState();
  const queryResult = useApiQuery<ForecastSolutionsListResponse>(
    tenantAlias,
    ApiEndpoint.SolutionsForecastList,
    {
      query: {
        limit: 100,
        offset: 0,
        model_name: state.model || undefined,
        model_version: state.modelVersion || undefined,
        output_frequency: state.frequency || undefined,
        output_hierarchy: state.hierarchy || undefined,
        status: 'production',
        target: state.target || undefined,
      },
    },
    { placeholderData: keepPreviousData, enabled },
  );

  const facets = useMemo<ForecastFacets>(() => {
    const solutions = queryResult.data?.forecast_solutions ?? [];
    if (!solutions.length) return EMPTY_FACETS;

    const forecastCreationTimes = uniqueSortedStrings(solutions.map((solution) => solution.fct ?? ''));
    const frequency = uniqueSortedStrings(solutions.map((solution) => solution.output_frequency ?? ''));
    const hierarchy = uniqueSortedStrings(solutions.map((solution) => solution.output_hierarchy ?? ''));
    const modelName = uniqueSortedStrings(solutions.map((solution) => solution.model_name ?? ''));
    const modelVersion = uniqueSortedStrings(solutions.map((solution) => solution.model_version ?? ''));
    const target = uniqueSortedStrings(solutions.map((solution) => solution.target ?? ''));

    const modelVersionByName = solutions.reduce<Record<string, string[]>>((acc, solution) => {
      const name = solution.model_name ?? '';
      const version = solution.model_version ?? '';
      if (!name || !version) return acc;
      acc[name] = uniqueSortedStrings([...(acc[name] || []), version]);
      return acc;
    }, {});

    const frequencyByModelAndVersion = solutions.reduce<Record<string, string[]>>((acc, solution) => {
      const name = solution.model_name ?? '';
      const version = solution.model_version ?? '';
      const value = solution.output_frequency ?? '';
      if (!name || !version || !value) return acc;
      const key = `${name}::${version}`;
      acc[key] = uniqueSortedStrings([...(acc[key] || []), value]);
      return acc;
    }, {});

    const hierarchyByModel = solutions.reduce<Record<string, string[]>>((acc, solution) => {
      const name = solution.model_name ?? '';
      const value = solution.output_hierarchy ?? '';
      if (!name || !value) return acc;
      acc[name] = uniqueSortedStrings([...(acc[name] || []), value]);
      return acc;
    }, {});

    const leadtimeSpansByModel = solutions.reduce<Record<string, Array<[number, number]>>>((acc, solution) => {
      const name = solution.model_name ?? '';
      if (!name) return acc;
      const pairs = toLeadtimeSpans(solution);
      const existing = acc[name] || [];
      const merged = [...existing, ...pairs];
      acc[name] = merged.filter((pair, i) => merged.findIndex(([lt, s]) => lt === pair[0] && s === pair[1]) === i);
      return acc;
    }, {});

    const leadtimeSpans = solutions
      .flatMap((solution) => toLeadtimeSpans(solution))
      .filter((pair, i, all) => all.findIndex(([lt, s]) => lt === pair[0] && s === pair[1]) === i);

    return {
      customers: [],
      forecastCreationTimes,
      frequency,
      frequencyByModelAndVersion,
      hierarchy,
      hierarchyByModel,
      leadtimeSpans,
      leadtimeSpansByModel,
      modelName,
      modelVersion,
      modelVersionByName,
      products: [],
      target,
    };
  }, [queryResult.data?.forecast_solutions]);

  const matchedSolutionId = useMemo(() => {
    const solutions = queryResult.data?.forecast_solutions ?? [];
    if (!solutions.length) return null;
    const fct = state.runDate || '';
    const matched = fct ? solutions.find((s) => s.fct === fct) : solutions[0];
    return matched?.solution_id ?? null;
  }, [queryResult.data?.forecast_solutions, state.runDate]);

  return { facets, isLoading: queryResult.isLoading, matchedSolutionId };
}
